import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../auth/authSelectors';

const API_URL = 'https://aadhavacademy.com/api/tournament/tournament_players.php';

export default function TournamentPlayers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const token = useSelector((state) => state.auth.access_token);
  
  // Extract user_id from auth state
  const userId = useSelector((state) => state.auth.user?.id);

  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [tournament, setTournament] = useState(null);

  // 1. DATE FORMATTER (Day-Mon-Year)
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '—';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${day}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  // 2. CSV EXPORT LOGIC (Only runs if user_id is 7)
  const exportToCSV = () => {
    if (userId !== 7) return;

    const headers = ["#", "Player Name", "DOB", "Category", "Status"];
    const rows = filteredPlayers.map((p, i) => [
      i + 1,
      p.player_name,
      formatDate(p.dob),
      p.category,
      "Confirmed"
    ]);

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${tournament?.name || 'roster'}_players.csv`);
    link.click();
  };

  useEffect(() => {
    if (!isAuthenticated || !token) { navigate('/login'); return; }
    
    fetch(`${API_URL}?tournament_id=${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          const list = data.data?.players || [];
          setPlayers(list);
          if (list.length > 0) {
            setTournament({ 
              name: list[0].tournament_name, 
              venue: list[0].venue, 
              date: list[0].event_date 
            });
          }
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [id, token, isAuthenticated, navigate]);

  const filteredPlayers = players.filter(p => 
    p.player_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="compact-page">
      <header className="compact-header">
        <div className="container-fluid">
          <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
          <div className="title-area">
            <h1>{tournament?.name || "Tournament Roster"}</h1>
            <p>{tournament?.venue} | {tournament?.date}</p>
          </div>
          
          <div className="action-group">
            {/* CSV Button restricted to user_id 7 */}
            {userId === 7 && (
              <button className="btn-csv" onClick={exportToCSV}>
                Export CSV
              </button>
            )}
            
            <div className="stat-box">
              <strong>{filteredPlayers.length}</strong>
              <span>Entries</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container-fluid">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Quick search player..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="table-wrapper">
          <table className="table-sm">
            <thead>
              <tr>
                <th style={{width: '50px'}}>#</th>
                <th>Player Name</th>
                {/* DOB Header restricted to user_id 7 */}
                {/* {userId === 7 && <th>Date of Birth</th>} */}
                <th>Category</th>
                <th className="text-end">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={userId === 7 ? 5 : 4} className="text-center py-5">Loading records...</td></tr>
              ) : filteredPlayers.length === 0 ? (
                <tr><td colSpan={userId === 7 ? 5 : 4} className="text-center py-5">No records found</td></tr>
              ) : (
                filteredPlayers.map((p, i) => (
                  <tr key={i}>
                    <td className="text-muted">{i + 1}</td>
                    <td className="fw-bold">{p.player_name}</td>
                    {/* DOB Data restricted to user_id 7 */}
                    {/* {userId === 7 && <td className="text-nowrap">{formatDate(p.dob)}</td>} */}
                    <td><span className="cat-text">{p.category}</span></td>
                    <td className="text-end">
                      <span className="status-label">Confirmed</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <style>{`
        /* ... styles remain same as previous compact version ... */
        .compact-page { background: #fff; min-height: 100vh; font-family: -apple-system, sans-serif; }
        .container-fluid { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .compact-header { background: #fff; border-bottom: 1px solid #eee; padding: 16px 0; position: sticky; top: 0; z-index: 100; }
        .compact-header .container-fluid { display: flex; align-items: center; gap: 20px; }
        .btn-back { background: #f8f9fa; border: 1px solid #ddd; padding: 6px 14px; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; }
        .title-area h1 { margin: 0; font-size: 20px; font-weight: 700; color: #111; }
        .title-area p { margin: 0; font-size: 12px; color: #666; }
        .action-group { margin-left: auto; display: flex; align-items: center; gap: 20px; }
        .btn-csv { background: #111; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
        .stat-box { text-align: right; line-height: 1.1; }
        .stat-box strong { display: block; font-size: 18px; color: #111; }
        .stat-box span { font-size: 10px; text-transform: uppercase; color: #999; font-weight: 700; }
        .search-bar { margin: 20px 0; }
        .search-bar input { width: 100%; max-width: 320px; padding: 8px 14px; border: 1px solid #e0e0e0; border-radius: 8px; font-size: 14px; outline: none; }
        .table-wrapper { border: 1px solid #eee; border-radius: 10px; overflow: auto; max-height: calc(100vh - 200px); }
        .table-sm { width: 100%; border-collapse: collapse; font-size: 13px; }
        .table-sm thead th { position: sticky; top: 0; background: #fcfcfc; color: #666; font-weight: 600; padding: 12px 15px; text-align: left; border-bottom: 2px solid #eee; z-index: 10; }
        .table-sm td { padding: 8px 15px; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .table-sm tbody tr:nth-child(even) { background-color: #fafafa; }
        .table-sm tbody tr:hover { background-color: #f0f7ff; }
        .fw-bold { font-weight: 600; color: #000; }
        .text-nowrap { white-space: nowrap; color: #444; font-family: monospace; }
        .text-end { text-align: right; }
        .status-label { color: #16a34a; font-weight: 700; font-size: 11px; text-transform: uppercase; }
        @media (max-width: 600px) {
          .table-sm th:nth-child(3), .table-sm td:nth-child(3) { display: none; }
          .title-area h1 { font-size: 16px; }
        }
      `}</style>
    </div>
  );
}